/**
 * Spec 088c — ScoreCard (Coverage-family triptych).
 *
 * Renders the three coverage-category scores (Coverage / Quality /
 * Topic Distribution) side-by-side using `ScoreBadge` so all visual
 * tokens come from the design-system registry.
 *
 * Consumed by spec 087 (Sitemap Coverage dashboard); shipped here so
 * the component exists when 087 is implemented and to demonstrate the
 * registry's exhaustiveness guarantee.
 *
 * Spec 087 gap 087-4: optional `previousValue` props thread through to
 * each ScoreBadge so the dashboard can show trend arrows when at least
 * two analyses exist in history. Omit / pass null when no prior run is
 * available — the badge collapses to its no-trend rendering.
 */
import { SCORE_DESIGN } from '../lib/score-design.js';
import { ScoreBadge } from './ScoreBadge.js';

export interface ScoreCardProps {
  /** CoverageScore value (0–100 scale), or null when not yet computed. */
  coverage: number | null;
  /** QualityScore value, or null. */
  quality: number | null;
  /** TopicDistributionScore value, or null. */
  topicDistribution: number | null;
  /** Optional override for the card heading. */
  title?: string;
  /** Optional subtitle (e.g. sample size, computed_at). */
  subtitle?: string;
  /** Optional className to extend card container styling. */
  className?: string;
  /** Optional prior Coverage score for trend arrow. */
  previousCoverage?: number | null;
  /** Optional prior Quality score for trend arrow. */
  previousQuality?: number | null;
  /** Optional prior Topic Distribution score for trend arrow. */
  previousTopicDistribution?: number | null;
}

export function ScoreCard({
  coverage,
  quality,
  topicDistribution,
  title = 'Coverage scores',
  subtitle,
  className = '',
  previousCoverage,
  previousQuality,
  previousTopicDistribution,
}: ScoreCardProps) {
  return (
    <section
      data-testid="score-card-triptych"
      className={`rounded-lg border border-slate-200 bg-white p-4  ${className}`}
    >
      <header className="mb-3">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {subtitle && (
          <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>
        )}
      </header>

      <dl className="grid grid-cols-3 gap-3">
        <div className="flex flex-col items-start gap-1">
          <dt className="text-xs font-medium text-slate-500">
            {SCORE_DESIGN.CoverageScore.label}
          </dt>
          <dd>
            <ScoreBadge
              scoreType="CoverageScore"
              value={coverage}
              previousValue={previousCoverage}
              size="lg"
            />
          </dd>
        </div>

        <div className="flex flex-col items-start gap-1">
          <dt className="text-xs font-medium text-slate-500">
            {SCORE_DESIGN.QualityScore.label}
          </dt>
          <dd>
            <ScoreBadge
              scoreType="QualityScore"
              value={quality}
              previousValue={previousQuality}
              size="lg"
            />
          </dd>
        </div>

        <div className="flex flex-col items-start gap-1">
          <dt className="text-xs font-medium text-slate-500">
            {SCORE_DESIGN.TopicDistributionScore.label}
          </dt>
          <dd>
            <ScoreBadge
              scoreType="TopicDistributionScore"
              value={topicDistribution}
              previousValue={previousTopicDistribution}
              size="lg"
            />
          </dd>
        </div>
      </dl>
    </section>
  );
}
